import { useState, useMemo } from "react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Check, ArrowLeft, BookOpen, Sparkles } from "lucide-react"
import Navigation from "@/components/Navigation"
import { useSearchParams, useNavigate } from "react-router-dom"
import { usePricing } from "@/hooks/usePricing"
import { formatPrice, formatDiscount } from "@/config/pricing"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

export default function CreateProduct() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const productId = searchParams.get("open") || "1"
  const [selectedFormat, setSelectedFormat] = useState("A4")
  const [selectedPages, setSelectedPages] = useState("24")
  const pricing = usePricing()

  const products = useMemo(() => ({
    "1": {
      name: "Le Pix",
      format: "13 x 13 cm",
      image: "/images/products/pix.png",
      originalPrice: formatPrice(pricing.pix.originalPrice),
      price: formatPrice(pricing.pix.price),
      discount: formatDiscount(pricing.pix.discount),
      color: "from-[#f9bb72]/20 to-[#f7bbac]/20",
      accent: "#f9bb72"
    },
    "2": {
      name: "L'Album Carré",
      format: "21 x 21 cm", 
      image: "/images/products/carre.png",
      originalPrice: formatPrice(pricing.albumCarre.originalPrice),
      price: formatPrice(pricing.albumCarre.price),
      discount: formatDiscount(pricing.albumCarre.discount),
      color: "from-blue-400/20 to-cyan-400/20",
      accent: "#60a5fa"
    },
    "3": {
      name: "L'Album Paysage",
      format: "A4",
      image: "/images/products/paysage.png", 
      originalPrice: formatPrice(pricing.albumPaysage.originalPrice),
      price: formatPrice(pricing.albumPaysage.price),
      discount: formatDiscount(pricing.albumPaysage.discount),
      color: "from-pink-500/20 to-rose-400/20",
      accent: "#ec4899"
    },
    "7": {
      name: "L'Album Paysage",
      format: "A4",
      image: "/images/products/paysage.png", 
      originalPrice: formatPrice(pricing.albumPaysage.originalPrice),
      price: formatPrice(pricing.albumPaysage.price),
      discount: formatDiscount(pricing.albumPaysage.discount),
      color: "from-pink-500/20 to-rose-400/20",
      accent: "#ec4899"
    },
    "8": {
      name: "L'Album PREMIUM",
      format: "20 x 20 cm",
      image: "/images/products/portrait.png", 
      originalPrice: formatPrice(pricing.albumPremium.originalPrice),
      price: formatPrice(pricing.albumPremium.price),
      discount: formatDiscount(pricing.albumPremium.discount),
      color: "from-green-500/20 to-emerald-400/20",
      accent: "#22c55e"
    },
    "9": {
      name: "Le YearBook",
      format: "A4",
      image: "/images/products/yearbook.png", 
      originalPrice: formatPrice(pricing.yearbook.originalPrice),
      price: formatPrice(pricing.yearbook.price),
      discount: formatDiscount(pricing.yearbook.discount),
      color: "from-rose-400/20 to-pink-300/20",
      accent: "#fb7185"
    }
  }), [pricing])
  
  const product = products[productId as keyof typeof products] || products["1"]

  const pageOptions = [
    { value: "24", label: "24 pages" },
    { value: "36", label: "36 pages" },
    { value: "48", label: "48 pages" },
    { value: "60", label: "60 pages" },
    { value: "72", label: "72 pages" },
    { value: "96", label: "96 pages" },
  ]

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section with Product */}
      <section className="relative min-h-[calc(100vh-80px)] flex items-center overflow-hidden">
        {/* Background Gradient */}
        <div className={`absolute inset-0 bg-gradient-to-br ${product.color}`}></div>
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent"></div>
        
        {/* Floating Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-[10%] w-48 h-48 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: `${product.accent}15` }}></div>
          <div className="absolute bottom-20 left-[5%] w-64 h-64 rounded-full blur-3xl animate-pulse delay-1000" style={{ backgroundColor: `${product.accent}10` }}></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 lg:px-8 py-8">
          {/* Back Button */}
          <button 
            onClick={() => navigate('/')}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 group text-sm"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span>Retour</span>
          </button>

          <div className="grid lg:grid-cols-[3fr_2fr] gap-8 lg:gap-12 items-center">
            {/* Left - Title, Subtitle, Price & Image */}
            <div className="space-y-6 order-2 lg:order-1 flex flex-col items-center">
              {/* Title & Price */}
              <div className="space-y-4 text-center">
                <div className="space-y-2">
                  <div className="flex items-center justify-center gap-2">
                    <Sparkles className="w-4 h-4" style={{ color: product.accent }} />
                    <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Album Photo Personnalisé
                    </span>
                  </div>
                  <h1 className="text-3xl md:text-4xl font-display font-bold text-foreground leading-tight">
                    {product.name}
                    <span className="text-gradient"> Rigide</span>
                  </h1>
                </div>
                <div className="flex items-baseline justify-center gap-3">
                  <span className="text-3xl md:text-4xl font-bold" style={{ color: product.accent }}>
                    {product.price}
                  </span>
                  <span className="text-lg text-muted-foreground line-through">
                    {product.originalPrice}
                  </span>
                </div>
              </div>

              {/* Product Image */}
              <div className="relative flex justify-center">
                <div 
                  className="absolute inset-0 blur-2xl opacity-20 scale-105"
                  style={{ backgroundColor: product.accent }}
                ></div>
                <div className="relative bg-card/60 backdrop-blur-sm rounded-2xl p-8 shadow-elegant border border-border/30">
                  <img 
                    src={product.image}
                    alt={product.name}
                    className="w-72 lg:w-96 h-auto drop-shadow-xl transform hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <Badge 
                  className="absolute -top-2 right-4 px-2.5 py-1 text-sm font-bold shadow-lg"
                  style={{ backgroundColor: product.accent }}
                >
                  {product.discount}
                </Badge>
              </div>
            </div>

            {/* Right - Format, Button & Features */}
            <div className="space-y-5 order-1 lg:order-2">
              {/* Options Card */}
              <div className="bg-card/60 backdrop-blur-sm rounded-xl p-5 border border-border/50 space-y-4">
                {/* Format Selection */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Format</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {["A4", "A5"].map((format) => (
                      <button
                        key={format}
                        onClick={() => setSelectedFormat(format)}
                        className={`relative p-3 rounded-lg border-2 transition-all duration-300 ${
                          selectedFormat === format
                            ? 'border-primary bg-primary/10 shadow-sm'
                            : 'border-border hover:border-primary/50 hover:bg-muted/50'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium text-sm">{format}</span>
                          {selectedFormat === format && (
                            <div 
                              className="w-5 h-5 rounded-full flex items-center justify-center"
                              style={{ backgroundColor: product.accent }}
                            >
                              <Check className="w-3 h-3 text-white" />
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 text-left">
                          {format === "A4" ? "21 × 29,7 cm" : "14,8 × 21 cm"}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Pages Selection */}
                <div className="space-y-3">
                  <Label className="text-sm font-semibold">Nombre de pages</Label>
                  <Select value={selectedPages} onValueChange={setSelectedPages}>
                    <SelectTrigger className="w-full h-10 text-sm bg-background/80">
                      <div className="flex items-center gap-2">
                        <BookOpen className="w-4 h-4 text-muted-foreground" />
                        <SelectValue placeholder="Sélectionnez" />
                      </div>
                    </SelectTrigger>
                    <SelectContent>
                      {pageOptions.map((option) => (
                        <SelectItem 
                          key={option.value} 
                          value={option.value}
                          className="py-2"
                        >
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* CTA Button */}
                <Button 
                  size="lg"
                  variant="rainbow"
                  className="w-full text-sm font-semibold shadow-elegant hover:shadow-glow transition-all duration-300 group"
                >
                  <BookOpen className="w-4 h-4 mr-2 group-hover:rotate-12 transition-transform" />
                  Commencer mon album
                </Button>
              </div>

              {/* Features - 4 icons */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { text: "Livraison offerte dès 50€", icon: "🚚" },
                  { text: "Qualité premium", icon: "✨" },
                  { text: "Personnalisation illimitée", icon: "🎨" },
                  { text: "Satisfait ou remboursé", icon: "💯" },
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-muted-foreground bg-card/40 rounded-lg px-3 py-2 border border-border/30">
                    <span>{feature.icon}</span>
                    <span>{feature.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}